import { CircularProgress, makeStyles } from '@material-ui/core'
import type { FindingResolutionState, SpecIntelligenceReport, SpecIntelligenceRunStatus } from 'api/graphQL/types/specIntelligence.types'
import type { SpecIntelligenceDrawerView } from 'components/SpecIntelligence/useSpecIntelligence'
import React, { useState } from 'react'
import SpecIntelligenceDisclaimer from './SpecIntelligenceDisclaimer'
import SpecIntelligenceDrawerHeader from './SpecIntelligenceDrawerHeader'
import SpecIntelligenceErrorView from './SpecIntelligenceErrorView'
import SpecIntelligencePickerView from './SpecIntelligencePickerView'
import SpecIntelligenceProgressView from './SpecIntelligenceProgressView'
import SpecIntelligenceResultsView from './SpecIntelligenceResultsView'
import { SPEC_INT_PANEL_BACKGROUND_COLOR } from './specIntelligenceConstants'

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: SPEC_INT_PANEL_BACKGROUND_COLOR,
    },
    content: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    loadingCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
}))

/** Props for {@link SpecIntelligenceDrawer}. */
export interface SpecIntelligenceDrawerProps {
    /** Called when the user clicks the close button or the panel is dismissed. */
    onClose: () => void
    /** Current view to display — owned by {@link useSpecIntelligence}. */
    drawerView: SpecIntelligenceDrawerView
    /** Latest run status from the parent hook; null when idle. */
    runStatus: SpecIntelligenceRunStatus | null
    /** Completed report for the results view; null when unavailable. */
    currentReport: SpecIntelligenceReport | null
    /**
     * Section numbers to pre-select in the picker when re-running — taken from
     * the previous report's analyzed sections.
     */
    rerunSectionNumbers?: string[] | null
    /** Start a new analysis run with the given section IDs. */
    onRunAnalysis: (selectedLeafIds: string[]) => void
    /**
     * Cancel the in-progress run from the progress view. Omit (or pass undefined) when there is
     * no cancelable run — the Cancel button is then hidden rather than rendered as a no-op.
     */
    onCancelRun?: () => void
    /** True while the cancel request is in flight — disables the Cancel button. */
    isCanceling?: boolean
    /** Reset back to the picker view from results view. */
    onRerun: () => void
    /** Sync a finding's resolution state to the parent hook on optimistic update and rollback. */
    onFindingStateChanged: (id: string, state: FindingResolutionState) => void
}

/**
 * Pure-display right-pane drawer for Spec Intelligence. Renders the view
 * (loading / picker / starting / progress / results / error) matching the `drawerView` prop.
 * All API calls live in {@link useSpecIntelligence} — this component has no
 * GraphQL dependencies.
 */
const SpecIntelligenceDrawer = ({
    onClose,
    drawerView,
    runStatus,
    currentReport,
    rerunSectionNumbers,
    onRunAnalysis,
    onCancelRun,
    isCanceling,
    onRerun,
    onFindingStateChanged,
}: SpecIntelligenceDrawerProps) => {
    const classes = useStyles()

    const [infoOpen, setInfoOpen] = useState(false)

    return (
        <div className={classes.container} role="region" aria-label="Insights">
            <SpecIntelligenceDrawerHeader
                onInfoOpen={() => setInfoOpen(true)}
                onClose={onClose}
                onRerun={drawerView === 'results' || drawerView === 'error' ? onRerun : undefined}
            />

            <div className={classes.content}>
                {infoOpen ? (
                    <SpecIntelligenceDisclaimer onDismiss={() => setInfoOpen(false)} />
                ) : (
                    <>
                        {drawerView === 'loading' && (
                            <div className={classes.loadingCenter}>
                                <CircularProgress size={32} aria-label="Loading" />
                            </div>
                        )}

                        {(drawerView === 'picker' || drawerView === 'starting') && (
                            <SpecIntelligencePickerView
                                onRunAnalysis={onRunAnalysis}
                                isStartingRun={drawerView === 'starting'}
                                initialSectionNumbers={rerunSectionNumbers ?? undefined}
                            />
                        )}

                        {drawerView === 'progress' && (
                            <SpecIntelligenceProgressView
                                status={runStatus}
                                onCancel={onCancelRun}
                                isCanceling={isCanceling}
                            />
                        )}

                        {drawerView === 'results' && currentReport && (
                            <SpecIntelligenceResultsView
                                report={currentReport}
                                onRerun={onRerun}
                                onFindingStateChanged={onFindingStateChanged}
                            />
                        )}

                        {drawerView === 'error' && (
                            <SpecIntelligenceErrorView
                                onRerun={onRerun}
                                errorType={currentReport?.errorType}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default SpecIntelligenceDrawer
